import {
  Box,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItemText,
  Chip,
  useTheme,
} from '@mui/material'
import { Settings, AutoAwesomeOutlined } from '@mui/icons-material'
import { MEMORY_CATEGORIES } from '../../../services/memoryService'
import { ThinScrollContainer } from '../../../styles/Common.styles'
import { StyledMemoryListItem } from './MemoryList.styles'

const MemoryList = ({ memories, isLoadingMemories, navigate }) => {
  const theme = useTheme()

  const getCategoryColor = category => {
    const colors = {
      preference: '#7c3aed', // Deeper purple
      instruction: '#2563eb', // Deeper blue
      fact: '#059669', // Deeper green
      summary: '#d97706', // Deeper orange
    }
    return colors[category] || theme.palette.text.secondary
  }

  const ListWrapper = Box

  return (
    <ThinScrollContainer>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          px: 1,
        }}
      >
        <Typography variant="h6" fontWeight="medium" sx={{ fontSize: 16 }}>
          Le Mie Memorie
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate('/memories')}
          sx={{
            fontSize: 12,
            minWidth: 'auto',
            px: 1,
            py: 0.5,
            borderRadius: 2,
            color: theme.palette.text.secondary,
            borderColor: theme.palette.divider,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            },
          }}
        >
          <Settings fontSize="small" sx={{ mr: 0.5 }} />
          Gestisci
        </Button>
      </Box>

      <ListWrapper>
        {/* Loading State */}
        {isLoadingMemories ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1.5}
            sx={{ p: 2 }}
          >
            <CircularProgress size={24} color="primary" />
            <Typography variant="body2" color="text.secondary">
              Caricamento memorie...
            </Typography>
          </Box>
        ) : memories.length === 0 ? (
          /* Empty State */
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={1}
              mb={1.5}
            >
              <AutoAwesomeOutlined sx={{ fontSize: 32, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                Nessuna memoria attiva.
              </Typography>
            </Box>
            <Button
              size="medium"
              variant="contained"
              fullWidth
              onClick={() => navigate('/memories')}
              sx={{ mt: 1, borderRadius: 2 }}
            >
              Crea Prima Memoria
            </Button>
          </Box>
        ) : (
          <List dense sx={{ p: 0 }}>
            {memories.map(memory => {
              const categoryLabel =
                MEMORY_CATEGORIES.find(c => c.value === memory.category)?.label ??
                memory.category

              return (
                <StyledMemoryListItem
                  key={memory.id}
                  onClick={() => navigate('/memories')}
                >
                  {/* Category Chip */}
                  <Box display="flex" alignItems="center" width="100%" mb={0.5}>
                    <Chip
                      label={categoryLabel}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(memory.category),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: 10,
                        height: 20,
                        borderRadius: '4px',
                      }}
                    />
                  </Box>

                  {/* Memory Content and Date */}
                  <ListItemText
                    sx={{ m: 0, width: '100%' }}
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        color="text.primary"
                        sx={{
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          fontSize: 13,
                        }}
                      >
                        {memory.content}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        {new Date(memory.created_at).toLocaleDateString('it-IT', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </Typography>
                    }
                  />
                </StyledMemoryListItem>
              )
            })}
          </List>
        )}
      </ListWrapper>
    </ThinScrollContainer>
  )
}

export default MemoryList
